<template>
  <!--
    Single Atlas brand-mark button used in any toolbar that should be
    able to open / toggle the global flyout. Concentrates the wiring
    (composable, connection-state colouring, suppressed behaviour,
    tooltip copy) in one place so additional surfaces — header,
    document modal, future skill manager, etc. — share a consistent
    look + behaviour without each re-implementing it.

    Uses a plain `title` attribute rather than `<v-tooltip>` to match
    the rest of the rm-portal header chrome (settings, search, etc.
    all rely on the native browser tooltip).

    Caller-controlled bits:
      - `tooltip` prop overrides the auto-generated hover text. Default
        adapts to flyout state (open/closed/suppressed) and connection
        state (live/offline) so each surface gets sensible copy without
        configuration.

    The icon's colour reflects the events-gateway connection state
    (mono SVG inheriting `currentColor`):
      - green: gateway connected, Atlas streams live updates.
      - grey:  gateway offline, Atlas still works via REST round-trips.
    Same information the previous header `sensors` icon carried, just
    on the brand mark instead of a generic broadcast glyph.
  -->
  <!-- Pill variant (prototype .v3-atlas-btn): fixed indigo gradient + label.
       Used in the V2 header; the doc toolbar keeps the icon variant below. -->
  <v-btn
    v-if="pill"
    class="atlas-pill"
    depressed
    :disabled="suppressed"
    :title="resolvedTooltip"
    :aria-label="resolvedAriaLabel"
    @click="onClick"
  >
    <AtlasLogo :size="16" variant="mono" class="atlas-pill-logo" />
    <span class="atlas-pill-label">Atlas</span>
    <span v-if="unreadCount > 0" class="atlas-pill-badge">{{ badgeContent }}</span>
  </v-btn>

  <v-btn
    v-else
    icon
    :class="buttonClass"
    :disabled="suppressed"
    :title="resolvedTooltip"
    :aria-label="resolvedAriaLabel"
    @click="onClick"
  >
    <v-badge
      :value="unreadCount > 0"
      :content="badgeContent"
      color="error"
      overlap
      offset-x="6"
      offset-y="6"
      bordered
    >
      <AtlasLogo :size="size" variant="mono" />
    </v-badge>
  </v-btn>
</template>

<script>
import { computed, getCurrentInstance } from 'vue';
import AtlasLogo from '@/components/agent/AtlasLogo.vue';
import { useAtlasFlyout } from '@/composables/agent/atlasFlyout';

export default {
  name: 'AtlasFlyoutButton',
  components: { AtlasLogo },
  props: {
    /**
     * Pixel size of the AtlasLogo SVG. Header chrome typically wants
     * 22; in-toolbar buttons (document modal) often look better at 20
     * to fit between the other icon-mode v-btns.
     */
    size: { type: [Number, String], default: 22 },
    /** Render the prototype indigo gradient "Atlas" pill (V2 header). */
    pill: { type: Boolean, default: false },
    /**
     * Override the auto-generated tooltip. Useful when a surface has
     * stronger context than the flyout state alone — e.g., "Ask Atlas
     * about this skill". Pass an empty string to suppress the tooltip
     * entirely (still leaves aria-label set).
     */
    tooltip: { type: String, default: null },
  },
  setup(props) {
    const flyout = useAtlasFlyout();
    const instance = getCurrentInstance();

    /**
     * Connection state lives on Vue.prototype.$eventGateway (set up by
     * `plugins/events.js`). Access it through the current instance's
     * proxy so this composable-API setup() doesn't need a `this`. The
     * gateway instance is reactive — its `connectionState` toggles
     * trigger re-evaluation of dependent computeds.
     */
    const gatewayConnected = computed(
      () => instance?.proxy?.$eventGateway?.connectionState ?? false,
    );

    const buttonClass = computed(() =>
      gatewayConnected.value
        ? 'atlas-flyout-btn atlas-flyout-btn--connected'
        : 'atlas-flyout-btn atlas-flyout-btn--offline',
    );

    /**
     * Tooltip / aria-label text. Prop wins; otherwise we synthesise
     * from the flyout state. The "live" / "offline" suffix tells the
     * user whether the gateway is connected — handy for diagnosing why
     * streaming might be missing.
     */
    const resolvedTooltip = computed(() => {
      if (props.tooltip != null) return props.tooltip;
      if (flyout.suppressed.value) return 'Atlas is the full page you are on';
      if (flyout.open.value) {
        return gatewayConnected.value
          ? 'Close Atlas (live)'
          : 'Close Atlas (events offline)';
      }
      return gatewayConnected.value
        ? 'Open Atlas (live)'
        : 'Open Atlas (events offline)';
    });

    /**
     * Badge cap. Anything past 9 collapses to "9+" so a noisy
     * background chain doesn't blow out the button's footprint.
     */
    const badgeContent = computed(() => {
      const n = flyout.unreadCount.value;
      return n > 9 ? '9+' : String(n);
    });

    /**
     * aria-label folds the unread count into the spoken/screen-reader
     * text so assistive tech announces e.g. "Open Atlas (live), 3 unread
     * messages". The visible title stays compact — the badge is the
     * sighted user's signal.
     */
    const resolvedAriaLabel = computed(() => {
      const n = flyout.unreadCount.value;
      if (n <= 0) return resolvedTooltip.value;
      const noun = n === 1 ? 'unread message' : 'unread messages';
      return `${resolvedTooltip.value}, ${n} ${noun}`;
    });

    function onClick() {
      flyout.toggle();
    }

    return {
      suppressed: flyout.suppressed,
      buttonClass,
      resolvedTooltip,
      resolvedAriaLabel,
      unreadCount: flyout.unreadCount,
      badgeContent,
      onClick,
    };
  },
};
</script>

<style scoped>
/*
 * The mono-variant AtlasLogo uses `currentColor`, so we drive the
 * fill/stroke from the wrapping btn's text colour. `::v-deep` reaches
 * inside the v-btn's nested `.v-btn__content` to get to the SVG.
 */
.atlas-flyout-btn ::v-deep .atlas-logo {
  transition: color 0.2s ease;
}
.atlas-flyout-btn--connected ::v-deep .atlas-logo {
  color: #10b981; /* emerald-500 — gateway connected, streaming live */
}
.atlas-flyout-btn--offline ::v-deep .atlas-logo {
  color: #9ca3af; /* gray-400 — gateway disconnected */
}

/* Pill variant — matches the beta prototype's .v3-atlas-btn (fixed indigo
   gradient, brand-independent), logo + label + optional unread badge. */
.atlas-pill.v-btn {
  height: 34px !important;
  min-width: 0;
  margin: 0 4px;
  align-self: center;
  padding: 0 14px 0 11px !important;
  border-radius: 999px !important;
  color: #fff !important;
  background: linear-gradient(135deg, #4f46e5, #a78bfa) !important;
  box-shadow: 0 2px 8px rgb(79 70 229 / 0.35);
  text-transform: none;
  letter-spacing: 0;
  font-weight: 650;
  font-size: 12.5px;
  position: relative;
}
.atlas-pill.v-btn::before { display: none; } /* drop Vuetify's hover overlay */
.atlas-pill.v-btn:hover { filter: brightness(1.06); }
.atlas-pill ::v-deep .v-btn__content { gap: 6px; }
.atlas-pill-logo { color: #fff; }
.atlas-pill-label { color: #fff; padding-top: 3px; }
.atlas-pill-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #c8447c;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px #16181d;
}
</style>
